import { useEffect, useState, type ReactNode } from "react";
import { Outlet } from "react-router-dom";

import { useAuth } from "./authContext";
import { consumeReturnTo, stashReturnTo } from "./returnTo";
import { Loading } from "@vellum/ui";

type RequireAuthProps = {
  /**
   * Rendered once the session check completes and no user is present.
   * Pass a `<LoginPage />` to render in place, or redirect (e.g. to Hub)
   * as a side effect and render a placeholder.
   */
  unauthenticated: () => ReactNode;
};

export function RequireAuth({ unauthenticated }: RequireAuthProps) {
  const { user, loading } = useAuth();
  // Set once we hand the browser off to the original page, so we render a
  // placeholder instead of flashing the protected route on the way out.
  const [returning, setReturning] = useState(false);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (!user) {
      // Stash before the user clicks through to Google — the OAuth flow comes
      // back to this origin without our query string.
      stashReturnTo();
      return;
    }

    const returnTo = consumeReturnTo();
    if (returnTo !== null) {
      setReturning(true);
      window.location.href = returnTo;
    }
  }, [user, loading]);

  if (loading || returning) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loading className="w-10 h-10" />
      </div>
    );
  }

  if (!user) {
    return <>{unauthenticated()}</>;
  }

  return <Outlet />;
}
